import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../root/routes";
import {
  AlertCard,
  Breadcrumbs,
  Button,
  Pill,
  SimpleCheckboxControlled,
  TextInputBase,
  Toggle,
  ToolTip,
  useSnackBar,
} from "@truevault/aquilo";
import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { DEEP_LINK_FOCUS_STYLE, useDataFlowsDeepLinkFocus } from "./dataFlowsDeepLink";
import { useQueryClient } from "@tanstack/react-query";
import { usePrimaryOrganizationId } from "../../../hooks";
import { RemoveVendorConfirmationModal } from "../vendor-inventory/RemoveVendorConfirmationModal";
import { DataMapPatchMutation, useDataMap } from "../../../../common/hooks/api/useDataMap";
import { useDataFlowsOnboardingFooterGate } from "./dataFlowsCompletion";
import { useInvalidateOnboardingProgress } from "../../../../common/hooks/useOnboardingProgress";
import {
  UseDisclosuresForCollectionGroups,
  useDisclosuresForCollectionGroups,
} from "../../../hooks/useDisclosuresForCollectionGroups";
import {
  useCollectionGroup,
  useEmploymentCollectionGroups,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataMapDto, PICGroup } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { disclosedPICGroupsFromFilteredDisclosed } from "../../../../common/service/resources/data-map/dataMapTransformers";
import _ from "lodash";
import { dataMapPendingDisclosedPicGroupIds } from "../../../../common/service/resources/data-map/dataMapPending";
import { disclosedRecommendationsForDataRecipient } from "../../../../common/service/resources/data-map/dataMapHelpers";
import { UUIDString } from "../../../../common/service/server/Types";
import { PageLoading } from "../../../components";
import {
  CheckboxItemProps,
  useCheckboxItemData,
} from "../../../components/organisms/DataRecipientDisclosureCheckboxes";
import clsx from "clsx";
import {
  groupAndSortVendorsByCategory,
  getCategoryGroupOrder,
} from "../../../util/vendorCategorization";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { useHistory, useParams } from "react-router";
import { DisclosuresExplanatoryCard } from "./DataFlowsExplanatoryCards";
import { ArrowLeftIcon, Cog6ToothIcon, TrashIcon } from "@heroicons/react/24/outline";
import { VENDOR_KEY_TRUEVAULT } from "../../../types/Vendor";
import {
  isVendorRemovable,
  removeVendorCompletely,
  vendorRemovalRequiresPageReload,
} from "../vendor-inventory/vendorInventoryUtils";
import { StandaloneVendorSettingsModal } from "../vendor-inventory/StandaloneVendorSettingsModal";

export type DataFlowsDisclosuresPageConfig = {
  collectionContext: CollectionContext[];
  dashboardRoute: string;
  breadcrumbSource: string;
  breadcrumbSourceRoute: string;
  headerElement?: React.ReactNode;
};

const CONSUMER_DATA_FLOWS_DISCLOSURES_CONFIG: DataFlowsDisclosuresPageConfig = {
  collectionContext: ["CONSUMER"],
  dashboardRoute: Routes.dataMap.dataFlows.dashboard,
  breadcrumbSource: "Data Flows",
  breadcrumbSourceRoute: Routes.dataMap.dataFlows.dashboard,
};

/**
 * Returns the collected PIC groups that are not yet disclosed under any vendor.
 * The disclosures flow also requires each displayed vendor to have at least one PIC group
 * selected (see getDataRecipientsWithNoDisclosedPICSelection). Empty here is only half of completeness.
 * checkbox logic used by VendorDisclosureCard / useVendorPICGroupDisclosureCheckboxState /
 * useCheckboxItemData:
 *   selectedPicGroupIds from useVendorPICGroupDisclosureCheckboxState(...)
 *   isChecked = selectedPicGroupIds.includes(picGroup.id)
 */
export function getIncompleteDisclosurePICGroups(
  dataMap: DataMapDto | undefined,
  dataRecipients: OrganizationDataRecipientDto[],
  groups: CollectionGroupDetailsDto[],
  collectedPICGroups: PICGroup[],
): PICGroup[] {
  if (!dataMap || !dataRecipients?.length || !collectedPICGroups?.length) {
    return collectedPICGroups ?? [];
  }

  const collectionGroupIds = groups.map((g) => g.id);

  const disclosedPICGroupIdsByAllVendors = new Set<string>();
  for (const dr of dataRecipients) {
    const disclosed = disclosedPICGroupsFromFilteredDisclosed(dataMap, {
      vendorId: dr.vendorId,
      collectionGroupId: collectionGroupIds,
    });
    for (const pg of disclosed) {
      disclosedPICGroupIdsByAllVendors.add(pg.id);
    }
  }

  return collectedPICGroups.filter((pg) => !disclosedPICGroupIdsByAllVendors.has(pg.id));
}

/** Returns whether a recipient has at least one disclosed PIC group in the given collection groups. */
export function hasDataRecipientDisclosedPICSelection(
  dataMap: DataMapDto,
  dataRecipient: OrganizationDataRecipientDto,
  groups: CollectionGroupDetailsDto[],
): boolean {
  const collectionGroupIds = groups.map((group) => group.id);
  return (
    disclosedPICGroupsFromFilteredDisclosed(dataMap, {
      vendorId: dataRecipient.vendorId,
      collectionGroupId: collectionGroupIds,
    }).length > 0
  );
}

/**
 * Returns data recipients shown on the disclosures flow who have no PIC groups disclosed to them yet.
 * When there are no collected PIC groups to assign, none are incomplete (vacuous).
 * Matches per-vendor `selectedPicGroupIds` from useVendorPICGroupDisclosureCheckboxState.
 */
export function getDataRecipientsWithNoDisclosedPICSelection(
  dataMap: DataMapDto | undefined,
  dataRecipients: OrganizationDataRecipientDto[],
  groups: CollectionGroupDetailsDto[],
  collectedPICGroups: PICGroup[],
): OrganizationDataRecipientDto[] {
  if (!dataMap || !dataRecipients?.length || !collectedPICGroups?.length) {
    return [];
  }
  return dataRecipients.filter(
    (dataRecipient) => !hasDataRecipientDisclosedPICSelection(dataMap, dataRecipient, groups),
  );
}

export function getDisclosureCompletionState(
  dataMap: DataMapDto | undefined,
  dataRecipients: OrganizationDataRecipientDto[],
  groups: CollectionGroupDetailsDto[],
  collectedPICGroups: PICGroup[],
) {
  const incompleteGroups = getIncompleteDisclosurePICGroups(
    dataMap,
    dataRecipients,
    groups,
    collectedPICGroups,
  );
  const recipientsWithNoDisclosedPIC = getDataRecipientsWithNoDisclosedPICSelection(
    dataMap,
    dataRecipients,
    groups,
    collectedPICGroups,
  );

  return {
    incompleteGroups,
    recipientsWithNoDisclosedPIC,
    isComplete: incompleteGroups.length === 0 && recipientsWithNoDisclosedPIC.length === 0,
  };
}

export function isDataRecipientDisclosureEnabled(
  dataMap: DataMapDto,
  dataRecipient: OrganizationDataRecipientDto,
  group: CollectionGroupDetailsDto | undefined,
) {
  if (!group) {
    return false;
  }

  return dataMap.recipientAssociations.some(
    (association) =>
      association.groupId == group.id &&
      association.vendorId == dataRecipient.vendorId &&
      association.noDataDisclosure !== true,
  );
}

export function getAssociatedDataRecipientsForDisclosure(
  dataMap: DataMapDto,
  dataRecipients: OrganizationDataRecipientDto[],
  group: CollectionGroupDetailsDto | undefined,
) {
  if (!group) {
    return [];
  }

  // `recipientAssociations` is the raw association table — the union with "vendors that have
  // disclosed PICs" happens on `dataMap.recipients`, not here. So a vendor the org owns but has
  // never associated with this group is absent, which is exactly what the Inactive section lists.
  const associatedVendorIds = new Set(
    dataMap.recipientAssociations
      .filter(
        (association) => association.groupId == group.id && association.noDataDisclosure !== true,
      )
      .map((association) => association.vendorId),
  );
  return dataRecipients.filter((dataRecipient) => associatedVendorIds.has(dataRecipient.vendorId));
}

/**
 * Derived state for per-vendor PIC-group disclosure checkboxes (selected, pending, recommended).
 * Safe when `dataMap` is still loading (returns empty arrays).
 */
export function useVendorPICGroupDisclosureCheckboxState(
  dataMap: DataMapDto | undefined,
  dataRecipient: OrganizationDataRecipientDto,
  groups: CollectionGroupDetailsDto[],
) {
  const collectionGroupIds = useMemo(() => groups.map((g) => g.id), [groups]);

  const selectedPicGroupIds = useMemo(() => {
    if (!dataMap) return [];
    const selectedPicGroups = disclosedPICGroupsFromFilteredDisclosed(dataMap, {
      vendorId: dataRecipient.vendorId,
      collectionGroupId: collectionGroupIds,
    });
    return _.uniq(selectedPicGroups.map((g) => g.id));
  }, [dataMap, dataRecipient.vendorId, collectionGroupIds]);

  const pendingPicGroupIds = useMemo(() => {
    if (!dataMap) return [];
    return dataMapPendingDisclosedPicGroupIds(dataMap, {
      vendorId: dataRecipient.vendorId,
      collectionGroupId: collectionGroupIds,
    });
  }, [dataMap, dataRecipient.vendorId, collectionGroupIds]);

  const recommendedGroups = useMemo((): PICGroup[] => {
    if (!dataMap) return [];
    return disclosedRecommendationsForDataRecipient(dataMap, dataRecipient, groups)
      .recommendedGroups;
  }, [dataMap, dataRecipient, groups]);

  // No sensitive-category carve-out on this step, unlike Step 1. Disclosure operates at PIC
  // *group* granularity and most groups hold at least one sensitive category, so excluding them
  // removed nearly every recommendation from the bulk action — the checkbox reported "checked"
  // while highlighted rows stayed empty, and cards whose only recommendation was such a group
  // lost the affordance entirely. A disclosure also only maps categories the org has already
  // said it collects onto a vendor the catalog says receives them, so it makes no new claim
  // about what data the business holds. The guard that matters — never asserting *collection* of
  // sensitive data from one click — stays on Step 1, where it works per category.
  return { selectedPicGroupIds, pendingPicGroupIds, recommendedGroups };
}

export function useVendorDisclosureCheckboxChangeHandler(
  disclosureData: UseDisclosuresForCollectionGroups,
  dataRecipient: OrganizationDataRecipientDto,
) {
  return useCallback(
    (disclosedId: UUIDString, disclosed: boolean) => {
      disclosureData.actions.updateDisclosure({
        vendorId: dataRecipient.vendorId,
        disclosedGroupIds: [disclosedId],
        disclosed,
      });
    },
    [disclosureData, dataRecipient.vendorId],
  );
}

export function VendorPICGroupDisclosureCheckbox({
  picGroup,
  dataRecipient,
  groups,
  selectedPicGroupIds,
  pendingPicGroupIds,
  recommendedGroups,
  dataMap,
  onChange,
  disabled = false,
}: {
  picGroup: PICGroup;
  dataRecipient: OrganizationDataRecipientDto;
  groups: CollectionGroupDetailsDto[];
  dataMap: DataMapDto;
  selectedPicGroupIds: string[];
  pendingPicGroupIds: string[];
  recommendedGroups: PICGroup[];
  onChange: (disclosedId: UUIDString, disclosed: boolean) => void;
  disabled?: boolean;
}) {
  const { description, isChecked, isLoading, recommended } = useCheckboxItemData({
    dataMap,
    collectionGroups: groups,
    picGroup,
    selected: selectedPicGroupIds,
    pending: pendingPicGroupIds,
    recommendedGroups,
  } as CheckboxItemProps);

  return (
    /* Recommended categories get the same purple highlight as the collection page (DES-30).
       Every row keeps the same padding and radius — only the fill changes — so the checkboxes
       line up down the card whether a row is recommended or not. */
    <div
      style={
        recommended
          ? { backgroundColor: "#F3F0FE", borderRadius: 6, padding: "6px 10px" }
          : { backgroundColor: "transparent", borderRadius: 6, padding: "6px 10px" }
      }
    >
      <div className="flex items-center gap-[6px]">
        <SimpleCheckboxControlled
          name={`${dataRecipient.vendorId}-${picGroup.key}-checkbox`}
          label={picGroup.name}
          checked={isChecked}
          onChange={(newChecked) => {
            onChange(picGroup.id, newChecked);
          }}
          disabled={isLoading || disabled}
        />
        {recommended && (
          <div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.6875 7.41602L14.7708 5.45768L12.8125 4.54102L14.7708 3.62435L15.6875 1.66602L16.6042 3.62435L18.5625 4.54102L16.6042 5.45768L15.6875 7.41602ZM17.4375 13.4368L16.8175 12.1194L15.5 11.4993L16.8175 10.8793L17.4375 9.56185L18.0575 10.8793L19.375 11.4993L18.0575 12.1194L17.4375 13.4368Z"
                fill="#3C1DA5"
              />
              <path
                d="M7.29167 19.1667C6.91667 19.1667 6.58681 19.0764 6.30208 18.8958C6.01736 18.7153 5.83333 18.4653 5.75 18.1458H5.45833C5.125 18.1458 4.83333 18.0208 4.58333 17.7708C4.33333 17.5208 4.20833 17.2292 4.20833 16.8958V13.9167C3.29167 13.3194 2.56944 12.5556 2.04167 11.625C1.51389 10.6944 1.25 9.66667 1.25 8.54167C1.25 6.86111 1.83681 5.43403 3.01042 4.26042C4.18403 3.08681 5.61111 2.5 7.29167 2.5C8.97222 2.5 10.3993 3.08681 11.5729 4.26042C12.7465 5.43403 13.3333 6.86111 13.3333 8.54167C13.3333 9.66667 13.0694 10.6944 12.5417 11.625C12.0139 12.5556 11.2917 13.3194 10.375 13.9167V16.8958C10.375 17.2292 10.25 17.5208 10 17.7708C9.75 18.0208 9.45833 18.1458 9.125 18.1458H8.83333C8.75 18.4653 8.56597 18.7153 8.28125 18.8958C7.99653 19.0764 7.66667 19.1667 7.29167 19.1667ZM5.45833 16.8958H9.125V15.9792H5.45833V16.8958ZM5.45833 15.1458H9.125V14.3125H5.45833V15.1458ZM5.27083 13.0625H6.8125V10.2083L4.89583 8.29167L5.54167 7.64583L7.29167 9.39583L9.04167 7.64583L9.6875 8.29167L7.77083 10.2083V13.0625H9.3125C10.1458 12.6736 10.816 12.0694 11.3229 11.25C11.8299 10.4306 12.0833 9.52778 12.0833 8.54167C12.0833 7.19444 11.6215 6.05903 10.6979 5.13542C9.77431 4.21181 8.63889 3.75 7.29167 3.75C5.94444 3.75 4.80903 4.21181 3.88542 5.13542C2.96181 6.05903 2.5 7.19444 2.5 8.54167C2.5 9.52778 2.75347 10.4306 3.26042 11.25C3.76736 12.0694 4.4375 12.6736 5.27083 13.0625Z"
                fill="#3C1DA5"
              />
            </svg>
          </div>
        )}
      </div>
      <div className="text-neutral-500 text-[14px] font-normal pl-[32px] leading-[20px]">
        {description}
      </div>
    </div>
  );
}

export function useVendorDisclosureCardControls({
  dataRecipient,
  groups,
  disclosureData,
  dataMap,
  patchDataMap,
  disabled = false,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  groups: CollectionGroupDetailsDto[];
  disclosureData: UseDisclosuresForCollectionGroups;
  dataMap: DataMapDto;
  patchDataMap: DataMapPatchMutation;
  disabled?: boolean;
}) {
  const { selectedPicGroupIds, pendingPicGroupIds, recommendedGroups } =
    useVendorPICGroupDisclosureCheckboxState(dataMap, dataRecipient, groups);
  const handlePICGroupDisclosureChange = useVendorDisclosureCheckboxChangeHandler(
    disclosureData,
    dataRecipient,
  );
  const [isSaving, setIsSaving] = useState(false);
  const collectionGroup = groups[0];
  const collectionGroupId = collectionGroup?.id;
  const controlsDisabled = isSaving || disabled;
  const toggleDisabled = !collectionGroupId || controlsDisabled;

  const handleDisclosureToggleChange = useCallback(
    async (checked: boolean) => {
      if (!collectionGroupId) {
        return;
      }
      setIsSaving(true);
      try {
        await patchDataMap.mutateAsync({
          operations: [
            {
              target: "RECIPIENT_ASSOCIATION",
              action: checked ? "ADD" : "REMOVE",
              groupId: collectionGroupId,
              vendorId: dataRecipient.vendorId,
            },
          ],
        });
      } finally {
        setIsSaving(false);
      }
    },
    [collectionGroupId, dataRecipient.vendorId, patchDataMap],
  );

  return {
    selectedPicGroupIds,
    pendingPicGroupIds,
    recommendedGroups,
    handlePICGroupDisclosureChange,
    collectionGroup,
    toggleDisabled,
    controlsDisabled,
    isSaving,
    handleDisclosureToggleChange,
  };
}

export function VendorDisclosureAssociationToggle({
  dataRecipient,
  collectionGroup,
  disclosureEnabled,
  disabled,
  onChange,
  rowClassName = "mt-[8px] flex items-center gap-[8px]",
  labelClassName = "text-[14px] font-medium text-neutral-700",
  labelContent,
  toggleSize = "small",
}: {
  dataRecipient: OrganizationDataRecipientDto;
  collectionGroup: CollectionGroupDetailsDto | undefined;
  disclosureEnabled: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void | Promise<void>;
  rowClassName?: string;
  labelClassName?: string;
  labelContent?: React.ReactNode;
  toggleSize?: "default" | "small";
}) {
  // TrueVault can't be removed as a disclosure vendor for non-employment collection groups (POL-7416)
  if (
    dataRecipient.vendorKey === VENDOR_KEY_TRUEVAULT &&
    collectionGroup?.collectionGroupType !== "EMPLOYMENT"
  ) {
    return null;
  }

  // Always rendered: this is the only control that attaches a vendor to a collection group or
  // detaches it. An earlier version hid it for single-group orgs on the theory that the
  // association is implicit there — it isn't (orgs have plenty of vendors attached to no group),
  // and hiding it stranded unassociated vendors and made attaching a one-way door.

  return (
    <div className={rowClassName}>
      <span className={labelClassName}>
        {labelContent ?? <>Collects data about {collectionGroup?.name}</>}
      </span>
      <Toggle
        checked={disclosureEnabled}
        disabled={disabled}
        label={`Collects data about ${collectionGroup?.name ?? "this collection group"}`}
        size={toggleSize}
        onChange={onChange}
      />
    </div>
  );
}

export function VendorDisclosurePICGroups({
  dataMap,
  picGroups,
  dataRecipient,
  groups,
  selectedPicGroupIds,
  pendingPicGroupIds,
  recommendedGroups,
  onChange,
  disabled = false,
  className = "mt-[12px] space-y-[4px]",
}: {
  dataMap: DataMapDto;
  picGroups: PICGroup[];
  dataRecipient: OrganizationDataRecipientDto;
  groups: CollectionGroupDetailsDto[];
  selectedPicGroupIds: string[];
  pendingPicGroupIds: string[];
  recommendedGroups: PICGroup[];
  onChange: (disclosedId: UUIDString, disclosed: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      {picGroups.map((picGroup) => {
        return (
          <div key={picGroup.id}>
            <VendorPICGroupDisclosureCheckbox
              dataMap={dataMap}
              picGroup={picGroup}
              dataRecipient={dataRecipient}
              groups={groups}
              selectedPicGroupIds={selectedPicGroupIds}
              pendingPicGroupIds={pendingPicGroupIds}
              recommendedGroups={recommendedGroups}
              onChange={onChange}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Per-vendor actions on the disclosures step, surfaced as visible link buttons (crit c_eaf714):
 * jump to the vendor's own settings, or remove it from the vendor list entirely. Both were
 * previously only reachable from the Vendor List page, which meant leaving the step to fix a
 * vendor you were looking at (DES-30).
 */
function VendorCardActions({
  dataRecipient,
  pageContext,
  onRemoved,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  pageContext: CollectionContext;
  onRemoved: (removedVendorId?: string) => void;
}) {
  const orgId = usePrimaryOrganizationId();
  const showSnackbar = useSnackBar();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await removeVendorCompletely(orgId, dataRecipient.vendorId);
      setConfirmRemoveOpen(false);
      if (vendorRemovalRequiresPageReload(dataRecipient.vendorKey)) {
        // Shopify Audiences / SNI removal has survey side-effects only a full reload picks up.
        window.location.reload();
        return;
      }
      onRemoved(dataRecipient.vendorId);
    } catch (e) {
      showSnackbar(`Couldn't remove ${dataRecipient.name}. Please try again.`, {
        variant: "error",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    /* This whole subtree — buttons and both modals — sits inside the vendor card's clickable
       header. Headless UI renders the modals through a portal, but React events still bubble up
       the component tree, so a click inside a modal would reach the header's onToggleCollapsed
       and unmount the modal underneath the user. `contents` keeps the wrapper out of the
       header's flex layout. */
    <div className="contents" onClick={(e) => e.stopPropagation()}>
      {/* Icon-only: these are edit-time actions, so they appear only while the card is open and
          name themselves on hover. */}
      <div className="flex items-center gap-[4px] flex-shrink-0">
        <ToolTip text="Vendor settings" inline>
          <button
            type="button"
            aria-label={`Vendor settings for ${dataRecipient.name}`}
            className="inline-flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border-none bg-transparent p-0 cursor-pointer text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
            onClick={(e) => {
              e.stopPropagation();
              setSettingsOpen(true);
            }}
          >
            <Cog6ToothIcon className="h-[18px] w-[18px] flex-shrink-0" />
          </button>
        </ToolTip>
        {isVendorRemovable(dataRecipient.vendorKey) && (
          <ToolTip text="Remove vendor" inline>
            <button
              type="button"
              aria-label={`Remove ${dataRecipient.name}`}
              className="inline-flex h-[28px] w-[28px] items-center justify-center rounded-[6px] border-none bg-transparent p-0 cursor-pointer text-neutral-500 hover:bg-red-50 hover:text-red-700"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmRemoveOpen(true);
              }}
            >
              <TrashIcon className="h-[18px] w-[18px] flex-shrink-0" />
            </button>
          </ToolTip>
        )}
      </div>
      {settingsOpen && (
        <StandaloneVendorSettingsModal
          key={dataRecipient.vendorId}
          vendorId={dataRecipient.vendorId}
          pageContext={pageContext}
          onClose={() => setSettingsOpen(false)}
          onChanged={onRemoved}
        />
      )}
      {confirmRemoveOpen && (
        <RemoveVendorConfirmationModal
          vendorName={dataRecipient.name}
          onConfirm={() => {
            if (!isRemoving) {
              void handleRemove();
            }
          }}
          onCancel={() => setConfirmRemoveOpen(false)}
        />
      )}
    </div>
  );
}

export type VendorDisclosureCardProps = {
  dataRecipient: OrganizationDataRecipientDto;
  groups: CollectionGroupDetailsDto[];
  disclosureData: UseDisclosuresForCollectionGroups;
  dataMap: DataMapDto;
  patchDataMap: DataMapPatchMutation;
  disclosureEnabled: boolean;
  headerAccessory?: React.ReactNode;
  /** Rendered inline right after the vendor name (e.g. the Incomplete pill, crit c_690567). */
  nameAccessory?: React.ReactNode;
  disabled?: boolean;
  /** Refresh callback for when the vendor is removed from the inventory from this card. */
  onVendorRemoved?: (removedVendorId?: string) => void;
  /** Collection context for the in-page vendor settings modal (crit c_98a5de). */
  pageContext?: CollectionContext;
  /** Accordion support (crit c_a7a7a2): when onToggleCollapsed is provided the header toggles,
      and a collapsed card renders as a compact summary row of its checked categories. */
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
};

export function VendorDisclosureCard({
  dataRecipient,
  groups,
  disclosureData,
  dataMap,
  patchDataMap,
  disclosureEnabled,
  headerAccessory,
  nameAccessory,
  disabled = false,
  onVendorRemoved,
  pageContext = "CONSUMER",
  isCollapsed = false,
  onToggleCollapsed,
}: VendorDisclosureCardProps) {
  const {
    selectedPicGroupIds,
    pendingPicGroupIds,
    recommendedGroups,
    handlePICGroupDisclosureChange,
    collectionGroup,
    toggleDisabled,
    controlsDisabled,
    isSaving,
    handleDisclosureToggleChange,
  } = useVendorDisclosureCardControls({
    dataRecipient,
    groups,
    disclosureData,
    dataMap,
    patchDataMap,
    disabled,
  });

  const collapsible = onToggleCollapsed != null;
  const showCollapsed = collapsible && isCollapsed;
  const selectedGroupNames = disclosureData.collectedPICGroups
    .filter((picGroup) => selectedPicGroupIds.includes(picGroup.id))
    .map((picGroup) => picGroup.name);

  // One shell for both states so open/close can animate: the summary row and the body swap via
  // opposing 1fr/0fr grid-row transitions — the CSS-only way to animate to auto height
  // (crit c_5e4326).
  const sectionTransition = {
    transition: "grid-template-rows 250ms ease",
  } as const;

  return (
    <div
      className={clsx(
        "p-[24px]",
        "rounded-[12px] border border-solid border-neutral-200",
        "bg-white shadow-[0px_1px_2px_0px_#1018280D]",
        showCollapsed && "cursor-pointer hover:border-neutral-400",
        (isSaving || disabled) && "pointer-events-none opacity-60",
      )}
      onClick={showCollapsed ? onToggleCollapsed : undefined}
    >
      <div className="min-w-0">
        <div
          className={clsx(
            "flex items-center justify-between gap-[12px] min-w-0",
            collapsible && "cursor-pointer",
          )}
          onClick={
            collapsible
              ? (e) => {
                  e.stopPropagation();
                  onToggleCollapsed();
                }
              : undefined
          }
        >
          <div className="flex items-center min-w-0">
            <img
              src={dataRecipient.logoUrl}
              style={{ width: "28px", height: "28px" }}
              className="flex-shrink-0"
            />
            <div className="text-[18px] font-medium text-neutral-900 ml-[12px] truncate">
              {dataRecipient.name}
            </div>
            {nameAccessory}
          </div>
          <div className="flex items-center gap-[12px] flex-shrink-0">
            {headerAccessory}
            {!showCollapsed && onVendorRemoved && (
              <>
                <VendorCardActions
                  dataRecipient={dataRecipient}
                  pageContext={pageContext}
                  onRemoved={onVendorRemoved}
                />
                {collapsible && <span className="h-[16px] w-px bg-neutral-300" aria-hidden />}
              </>
            )}
            {collapsible && (
              <>
                {showCollapsed && disclosureEnabled && (
                  <span className="text-[12px] font-medium text-neutral-500 whitespace-nowrap">
                    {selectedGroupNames.length}{" "}
                    {selectedGroupNames.length === 1 ? "disclosure" : "disclosures"}
                  </span>
                )}
                {/* Edit opens the card; Done closes it — and, on the Disclosures page, moves a
                    finished card into its category (see GroupedVendorDisclosureCards). */}
                <Button
                  color="link"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCollapsed();
                  }}
                >
                  {showCollapsed ? "Edit" : "Done"}
                </Button>
              </>
            )}
          </div>
        </div>

        {collapsible && (
          <div
            aria-hidden={!showCollapsed}
            style={{
              display: "grid",
              gridTemplateRows: showCollapsed ? "1fr" : "0fr",
              ...sectionTransition,
            }}
          >
            <div
              style={{
                overflow: "hidden",
                minHeight: 0,
                visibility: showCollapsed ? "visible" : "hidden",
                transition: "visibility 250ms",
              }}
            >
              <div className="flex flex-wrap items-center gap-[6px] mt-[10px] ml-[40px]">
                {!disclosureEnabled ? (
                  <span className="text-[13px] text-neutral-500">
                    Not receiving data from this group
                  </span>
                ) : selectedGroupNames.length > 0 ? (
                  selectedGroupNames.map((name) => (
                    <span
                      key={name}
                      className="text-[12px] font-medium"
                      style={{
                        padding: "2px 10px",
                        borderRadius: 999,
                        backgroundColor: "#F5F4F9",
                        border: "1px solid #E8E5EE",
                        color: "#534E65",
                      }}
                    >
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="text-[13px] text-neutral-500">No categories selected yet</span>
                )}
              </div>
            </div>
          </div>
        )}

        <div
          aria-hidden={showCollapsed}
          style={{
            display: "grid",
            gridTemplateRows: showCollapsed ? "0fr" : "1fr",
            ...sectionTransition,
          }}
        >
          <div
            style={{
              overflow: "hidden",
              minHeight: 0,
              visibility: showCollapsed ? "hidden" : "visible",
              transition: "visibility 250ms",
            }}
          >
            <VendorDisclosureAssociationToggle
              dataRecipient={dataRecipient}
              collectionGroup={collectionGroup}
              disclosureEnabled={disclosureEnabled}
              disabled={toggleDisabled}
              onChange={handleDisclosureToggleChange}
            />
            {disclosureEnabled && recommendedGroups.length > 0 && (
              /* One-click accept for the categories this vendor typically receives — writes through
                 the same disclosure update as manual checking (DES-30). */
              <div
                className="mt-[12px] inline-flex items-center"
                style={{ backgroundColor: "#F3F0FE", borderRadius: 6, padding: "4px 12px" }}
              >
                <SimpleCheckboxControlled
                  containerClassName="items-center gap-[6px]"
                  name={`${dataRecipient.vendorId}-select-recommended`}
                  checked={recommendedGroups.every((g) => selectedPicGroupIds.includes(g.id))}
                  onChange={(newChecked) => {
                    const ids = recommendedGroups
                      .filter((g) =>
                        newChecked
                          ? !selectedPicGroupIds.includes(g.id)
                          : selectedPicGroupIds.includes(g.id),
                      )
                      .map((g) => g.id);
                    if (ids.length > 0) {
                      disclosureData.actions.updateDisclosure({
                        vendorId: dataRecipient.vendorId,
                        disclosedGroupIds: ids,
                        disclosed: newChecked,
                      });
                    }
                  }}
                  disabled={controlsDisabled}
                  label={
                    <span className="flex items-center gap-[4px] text-[13px] font-medium text-primary-800 whitespace-nowrap">
                      Check all recommended
                      <span className="text-primary-600">{LightbulbIcon}</span>
                    </span>
                  }
                />
              </div>
            )}
            {disclosureEnabled && (
              <VendorDisclosurePICGroups
                dataMap={dataMap}
                picGroups={disclosureData.collectedPICGroups}
                dataRecipient={dataRecipient}
                groups={groups}
                selectedPicGroupIds={selectedPicGroupIds}
                pendingPicGroupIds={pendingPicGroupIds}
                recommendedGroups={recommendedGroups}
                onChange={handlePICGroupDisclosureChange}
                disabled={controlsDisabled}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DataFlowsVendorDisclosureCard({
  isIncomplete,
  ...vendorDisclosureCardProps
}: Omit<VendorDisclosureCardProps, "headerAccessory" | "nameAccessory"> & {
  isIncomplete: boolean;
}) {
  return (
    <VendorDisclosureCard
      {...vendorDisclosureCardProps}
      nameAccessory={
        isIncomplete && (
          <span className="ml-[10px] flex-shrink-0">
            <Pill variant="warning" noMargin>
              Incomplete
            </Pill>
          </span>
        )
      }
    />
  );
}

export function NavCountBadge({
  count,
  tone,
  label,
}: {
  count: number;
  tone: "amber" | "neutral";
  /** Optional noun after the number, e.g. "sources" -> "2 sources" (crit c_df5156). */
  label?: string;
}) {
  return (
    <span
      className="inline-flex items-center justify-center flex-shrink-0 text-[11px] font-semibold whitespace-nowrap"
      style={{
        minWidth: 20,
        height: 20,
        padding: "0 6px",
        borderRadius: 999,
        backgroundColor: tone === "amber" ? "#FEF3C7" : "#EEEDF2",
        color: tone === "amber" ? "#92400E" : "#534E65",
      }}
    >
      {label ? `${count} ${label}` : count}
    </span>
  );
}

function GroupedVendorDisclosureCards({
  associatedDataRecipients,
  unassociatedDataRecipients,
  dataMap,
  groups,
  disclosureData,
  patchDataMap,
  dashboardRoute,
  pageContext,
  hasMultipleGroups = false,
  showHrHelpText = false,
}: {
  associatedDataRecipients: OrganizationDataRecipientDto[];
  unassociatedDataRecipients: OrganizationDataRecipientDto[];
  dataMap: DataMapDto;
  groups: CollectionGroupDetailsDto[];
  disclosureData: UseDisclosuresForCollectionGroups;
  patchDataMap: DataMapPatchMutation;
  dashboardRoute: string;
  pageContext: CollectionContext;
  /** Multi-group orgs open every card so per-group toggles are visible (crit c_cf6100). */
  hasMultipleGroups?: boolean;
  showHrHelpText: boolean;
}) {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const queryClient = useQueryClient();
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  // A removed vendor changes both the vendor list and the data map, so re-read both. The prefix
  // match covers every option variant of the vendors query key.
  const handleVendorRemoved = useCallback(
    (removedVendorId?: string) => {
      void queryClient.invalidateQueries({ queryKey: ["organizationVendors", orgId] });
      void queryClient.invalidateQueries({ queryKey: ["dataMap", orgId] });
      // The sources page reads this too, and it is restored from persisted cache on navigation.
      void queryClient.invalidateQueries({ queryKey: ["orgDataSources", orgId] });
      // Don't leave collapse or section state pointing at a card that no longer exists.
      if (removedVendorId) {
        latchedSectionByVendorId.current.delete(removedVendorId);
        setCollapsedVendorIds((prev) => {
          if (!prev.has(removedVendorId)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(removedVendorId);
          return next;
        });
      }
    },
    [queryClient, orgId],
  );
  const dataRecipients = useMemo(() => {
    return _.uniqBy([...associatedDataRecipients, ...unassociatedDataRecipients], "vendorId");
  }, [associatedDataRecipients, unassociatedDataRecipients]);
  // Arriving from the finished data-map diagram: scroll to the vendor that was clicked.
  const focusedVendorId = useDataFlowsDeepLinkFocus("vendor", "vendor", dataRecipients.length > 0);

  const groupedVendors = useMemo(() => {
    return groupAndSortVendorsByCategory(dataRecipients);
  }, [dataRecipients]);

  const filteredGroupedVendors = useMemo(() => {
    const normalizedSearchTerm = vendorSearchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) {
      return groupedVendors;
    }

    return Object.entries(groupedVendors).reduce((acc, [groupName, vendors]) => {
      const matchingVendors = vendors.filter((vendor) =>
        vendor.name.toLowerCase().includes(normalizedSearchTerm),
      );
      if (matchingVendors.length > 0) {
        acc[groupName] = matchingVendors;
      }
      return acc;
    }, {} as Record<string, OrganizationDataRecipientDto[]>);
  }, [groupedVendors, vendorSearchTerm]);

  const associatedVendorIds = useMemo(() => {
    return new Set(associatedDataRecipients.map((dataRecipient) => dataRecipient.vendorId));
  }, [associatedDataRecipients]);

  const sortedGroupNames = useMemo(() => {
    return Object.keys(filteredGroupedVendors).sort(
      (a, b) => getCategoryGroupOrder(a) - getCategoryGroupOrder(b),
    );
  }, [filteredGroupedVendors]);

  const { incompleteGroups, recipientsWithNoDisclosedPIC } = getDisclosureCompletionState(
    dataMap,
    associatedDataRecipients,
    groups,
    disclosureData.collectedPICGroups,
  );
  const incompleteVendorIds = new Set(recipientsWithNoDisclosedPIC.map((r) => r.vendorId));

  // Every card that still needs input — nothing selected yet, whether inactive or toggled on
  // without categories — starts open so it's one less click to configure (POL-7818 item 4);
  // already-configured cards start collapsed. Multi-group orgs keep starting with every card
  // open so per-group toggles stay visible (crit c_cf6100). Each card collapses independently.
  //
  // Collapse state is latched per vendor rather than derived: the Inactive list reorders as
  // vendors are toggled on, so a derived value would silently close the card the user was
  // working in.
  const [collapsedVendorIds, setCollapsedVendorIds] = useState<Set<string>>(new Set());
  const hasSeededCollapsed = useRef(false);

  useLayoutEffect(() => {
    if (hasSeededCollapsed.current || dataRecipients.length === 0) {
      return;
    }
    hasSeededCollapsed.current = true;
    if (hasMultipleGroups) {
      return;
    }
    setCollapsedVendorIds(
      new Set(
        dataRecipients
          .filter(
            (dr) => associatedVendorIds.has(dr.vendorId) && !incompleteVendorIds.has(dr.vendorId),
          )
          .map((dr) => dr.vendorId),
      ),
    );
  }, [dataRecipients, hasMultipleGroups, associatedVendorIds, incompleteVendorIds]);

  // Two pinned sections sit above the categories: "Incomplete" (toggled on, no categories yet)
  // and "Inactive" (not receiving this group's data, crit c_e67865); the category sections hold
  // only finished vendors.
  //
  // A vendor's section is latched while its card is open and re-derived only once the card is
  // collapsed, so finishing a card (or toggling one on) never yanks it out from under the user
  // mid-edit — it moves when they close it. Held in a ref and reconciled during render: the
  // rule is a pure function of state that is already rendering, so a second render would only
  // repeat it.
  type VendorSection = "incomplete" | "inactive" | "category";
  const latchedSectionByVendorId = useRef(new Map<string, VendorSection>());
  // Cards mid-move keep their old section for one more beat so they can fade out of it; the
  // card that just landed gets an enter animation in its new section.
  const [movingVendorIds, setMovingVendorIds] = useState<Set<string>>(new Set());
  const [landedVendorId, setLandedVendorId] = useState<string | null>(null);
  const targetSectionFor = (vendorId: string): VendorSection =>
    !associatedVendorIds.has(vendorId)
      ? "inactive"
      : incompleteVendorIds.has(vendorId)
      ? "incomplete"
      : "category";
  const sectionFor = (vendorId: string): VendorSection => {
    const target = targetSectionFor(vendorId);
    const latched = latchedSectionByVendorId.current.get(vendorId);
    if (
      latched === undefined ||
      (collapsedVendorIds.has(vendorId) && !movingVendorIds.has(vendorId))
    ) {
      latchedSectionByVendorId.current.set(vendorId, target);
      return target;
    }
    return latched;
  };
  const incompleteVendors = sortedGroupNames.flatMap((groupName) =>
    filteredGroupedVendors[groupName].filter((dr) => sectionFor(dr.vendorId) === "incomplete"),
  );
  const inactiveVendors = sortedGroupNames.flatMap((groupName) =>
    filteredGroupedVendors[groupName].filter((dr) => sectionFor(dr.vendorId) === "inactive"),
  );
  const activeGroupedVendors: Record<string, OrganizationDataRecipientDto[]> = {};
  for (const groupName of sortedGroupNames) {
    const active = filteredGroupedVendors[groupName].filter(
      (dr) => sectionFor(dr.vendorId) === "category",
    );
    if (active.length > 0) {
      activeGroupedVendors[groupName] = active;
    }
  }
  const activeGroupNames = Object.keys(activeGroupedVendors);

  // A deep link resolves asynchronously (the anchor is polled for), so it can land after the
  // seed — make sure the targeted card is open.
  useEffect(() => {
    if (focusedVendorId) {
      setCollapsedVendorIds((prev) => {
        if (!prev.has(focusedVendorId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(focusedVendorId);
        return next;
      });
    }
  }, [focusedVendorId]);

  const isCardCollapsed = (vendorId: string) => collapsedVendorIds.has(vendorId);
  const expandCard = (vendorId: string) => {
    setCollapsedVendorIds((prev) => {
      if (!prev.has(vendorId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(vendorId);
      return next;
    });
  };
  // Done on a pinned card: collapse it, let it fade out of its section, land it in its new one,
  // then open the next card that still needs work and bring it into view. A card whose section
  // doesn't change (Done on a category card, or still incomplete) just collapses.
  const MOVE_MS = 260;
  // The move animation's timers, cleared on unmount so they don't set state on a gone page.
  const finishTimersRef = useRef<number[]>([]);
  useEffect(() => () => finishTimersRef.current.forEach((id) => window.clearTimeout(id)), []);
  const later = (fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      finishTimersRef.current = finishTimersRef.current.filter((pending) => pending !== id);
      fn();
    }, ms);
    finishTimersRef.current.push(id);
  };
  const finishCard = (vendorId: string) => {
    const latched = latchedSectionByVendorId.current.get(vendorId);
    const target = targetSectionFor(vendorId);
    const moves = latched !== undefined && latched !== target;
    const pinnedOrder = [...incompleteVendors, ...inactiveVendors]
      .map((dr) => dr.vendorId)
      .filter((id) => id !== vendorId);

    setCollapsedVendorIds((prev) => new Set(prev).add(vendorId));
    if (!moves) {
      return;
    }
    setMovingVendorIds((prev) => new Set(prev).add(vendorId));
    later(() => {
      setMovingVendorIds((prev) => {
        const next = new Set(prev);
        next.delete(vendorId);
        return next;
      });
      setLandedVendorId(vendorId);
      later(() => setLandedVendorId((cur) => (cur === vendorId ? null : cur)), 400);

      const nextVendorId = pinnedOrder[0];
      if (nextVendorId) {
        expandCard(nextVendorId);
        later(() => {
          document
            .getElementById(`vendor-${nextVendorId}`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 50);
      }
    }, MOVE_MS);
  };
  const toggleCard = (vendorId: string) => {
    if (collapsedVendorIds.has(vendorId)) {
      expandCard(vendorId);
    } else {
      finishCard(vendorId);
    }
  };

  const menuItem = (
    key: string,
    contents: string,
    isSelected: boolean,
    onClick: () => void,
    badge?: React.ReactNode,
  ) => {
    return (
      <div
        key={key}
        className={`w-[256px] min-h-[40px] py-[10px] px-[12px] text-[12px] rounded-[8px] font-medium cursor-pointer flex items-center justify-between gap-[12px] ${
          isSelected ? "text-neutral-900 bg-neutral-100" : "text-neutral-600"
        }`}
        style={{ marginRight: "30px" }}
        onClick={() => onClick && onClick()}
      >
        <span className="min-w-0 truncate">{contents}</span>
        {badge}
      </div>
    );
  };

  const scrollToSection = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderVendorCard = (dataRecipient: OrganizationDataRecipientDto) => (
    <div
      key={dataRecipient.vendorId}
      id={`vendor-${dataRecipient.vendorId}`}
      className={clsx(
        "w-full",
        landedVendorId === dataRecipient.vendorId && "animate-land-in",
        movingVendorIds.has(dataRecipient.vendorId) && "opacity-0 -translate-y-[6px]",
      )}
      style={{
        transition: `opacity ${MOVE_MS}ms ease, transform ${MOVE_MS}ms ease`,
        ...(focusedVendorId === dataRecipient.vendorId ? DEEP_LINK_FOCUS_STYLE : {}),
      }}
    >
      <DataFlowsVendorDisclosureCard
        dataMap={dataMap}
        groups={groups}
        disclosureData={disclosureData}
        dataRecipient={dataRecipient}
        patchDataMap={patchDataMap}
        disclosureEnabled={associatedVendorIds.has(dataRecipient.vendorId)}
        isIncomplete={incompleteVendorIds.has(dataRecipient.vendorId)}
        onVendorRemoved={handleVendorRemoved}
        pageContext={pageContext}
        isCollapsed={isCardCollapsed(dataRecipient.vendorId)}
        onToggleCollapsed={() => toggleCard(dataRecipient.vendorId)}
      />
    </div>
  );

  return (
    <div className="flex">
      <div className="sticky top-[24px] self-start">
        <div
          className="text-neutral-900 font-medium text-[14px] flex items-center cursor-pointer gap-[12px] ml-[12px] mb-[12px]"
          onClick={() => {
            history.push(dashboardRoute);
          }}
        >
          <ArrowLeftIcon className="w-[20px] h-[20px]" />
          Back to Data Flows
        </div>
        <div className="w-[256px] mb-[12px]" style={{ marginRight: "30px" }}>
          <TextInputBase
            field="vendorSearch"
            type="search"
            placeholder="Search vendors"
            value={vendorSearchTerm}
            inputHeight="h-[36px]"
            inputTextSize="text-[14px]"
            onChange={(e) => setVendorSearchTerm(e.target.value)}
          />
        </div>
        {incompleteVendors.length > 0 &&
          menuItem(
            "__incomplete",
            "Incomplete",
            false,
            () => scrollToSection("vendor-group-incomplete"),
            <NavCountBadge count={incompleteVendors.length} tone="amber" />,
          )}
        {inactiveVendors.length > 0 &&
          menuItem(
            "__inactive",
            "Inactive",
            false,
            () => scrollToSection("vendor-group-inactive"),
            <NavCountBadge count={inactiveVendors.length} tone="amber" />,
          )}
        {activeGroupNames.map((groupName) =>
          menuItem(
            groupName,
            groupName,
            false,
            () => scrollToSection(`vendor-group-${groupName}`),
            <NavCountBadge count={activeGroupedVendors[groupName].length} tone="neutral" />,
          ),
        )}
      </div>
      <div className="flex flex-col">
        <div className="max-w-[800px]">
          <DisclosuresExplanatoryCard isHr={showHrHelpText} />
        </div>

        {vendorSearchTerm.trim() && sortedGroupNames.length === 0 && (
          <div className="max-w-[800px] text-[14px] text-neutral-500 mb-[24px]">
            No vendors found matching "{vendorSearchTerm}".
          </div>
        )}

        {incompleteVendors.length > 0 && (
          <div id="vendor-group-incomplete" className="mb-[24px]">
            <div className="flex items-center gap-[8px]">
              <h3 className="text-[18px] font-semibold text-neutral-900">Incomplete</h3>
              <NavCountBadge count={incompleteVendors.length} tone="amber" />
            </div>
            <div className="text-[14px] text-neutral-500 mb-[24px] mt-[4px]">
              Select at least one category for each vendor — it moves into its category below when
              you close the card.
            </div>
            <div className="flex max-w-[800px] flex-col gap-[12px]">
              {incompleteVendors.map((dataRecipient) => renderVendorCard(dataRecipient))}
            </div>
          </div>
        )}

        {inactiveVendors.length > 0 && (
          <div id="vendor-group-inactive" className="mb-[24px]">
            <div className="flex items-center gap-[8px]">
              <h3 className="text-[18px] font-semibold text-neutral-900">Inactive</h3>
              <NavCountBadge count={inactiveVendors.length} tone="amber" />
            </div>
            <div className="text-[14px] text-neutral-500 mb-[24px] mt-[4px]">
              Toggle on the vendors that receive this group's data — they move on when you close the
              card.
            </div>
            <div className="flex max-w-[800px] flex-col gap-[12px]">
              {inactiveVendors.map((dataRecipient) => renderVendorCard(dataRecipient))}
            </div>
          </div>
        )}

        {activeGroupNames.map((groupName) => (
          <div key={groupName} id={`vendor-group-${groupName}`} className="mb-[24px]">
            <div className="flex items-center gap-[8px] mb-[24px]">
              <h3 className="text-[18px] font-semibold text-neutral-900">{groupName}</h3>
              <NavCountBadge
                count={activeGroupedVendors[groupName].length}
                label={
                  activeGroupedVendors[groupName].length === 1
                    ? "Data Recipient"
                    : "Data Recipients"
                }
                tone="neutral"
              />
            </div>
            <div className="flex max-w-[800px] flex-col gap-[12px]">
              {activeGroupedVendors[groupName].map((dataRecipient) =>
                renderVendorCard(dataRecipient),
              )}
            </div>
          </div>
        ))}

        <div className="mt-[24px] max-w-[800px]">
          {(incompleteGroups.length > 0 || recipientsWithNoDisclosedPIC.length > 0) && (
            <AlertCard variant="warning" containerClasses="!bg-amber-100">
              <>
                {incompleteGroups.length > 0 && (
                  <div className={recipientsWithNoDisclosedPIC.length > 0 ? "mb-4" : undefined}>
                    <div className="font-semibold mb-1">
                      The following personal information categories are not selected for any vendor:
                    </div>
                    <ul>
                      {incompleteGroups.map((group) => (
                        <li key={group.id}>{group.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {recipientsWithNoDisclosedPIC.length > 0 && (
                  <div>
                    <div className="font-semibold mb-1">
                      The following vendors have no personal information categories selected yet:
                    </div>
                    <ul>
                      {recipientsWithNoDisclosedPIC.map((r) => (
                        <li key={r.vendorId}>{r.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            </AlertCard>
          )}
        </div>
      </div>
    </div>
  );
}

export function DataFlowsDisclosuresPage() {
  return <DataFlowsDisclosuresPageContent config={CONSUMER_DATA_FLOWS_DISCLOSURES_CONFIG} />;
}

export function DataFlowsDisclosuresPageContent({
  config,
}: {
  config: DataFlowsDisclosuresPageConfig;
}) {
  const history = useHistory();

  const orgId = usePrimaryOrganizationId();
  const { groupId } = useParams<{ groupId: UUIDString }>();
  const [group] = useCollectionGroup(orgId, groupId);
  const groups = useMemo(() => (group ? [group] : []), [group]);

  // Multi-group orgs open every vendor card by default so the per-group toggles are visible
  // (crit c_cf6100), and the group count decides whether the association toggle is meaningful.
  // useCollectionGroups does not honor an "only use settled" flag, so track the request itself:
  // rendering the cards before this resolves would start them in the wrong mode and then flip.
  const [nonEmploymentGroups, nonEmploymentGroupsRequest] = useNonEmploymentCollectionGroups(orgId);
  const [employmentGroups, employmentGroupsRequest] = useEmploymentCollectionGroups(orgId);
  const isEmploymentContext = config.collectionContext.includes("EMPLOYEE");
  const contextGroups = isEmploymentContext ? employmentGroups : nonEmploymentGroups;
  const contextGroupsRequest = isEmploymentContext
    ? employmentGroupsRequest
    : nonEmploymentGroupsRequest;
  const hasMultipleGroups = contextGroups.length > 1;
  const {
    data: dataMap,
    mutators: { patch: patchDataMap },
  } = useDataMap(orgId);
  const disclosureData = useDisclosuresForCollectionGroups(orgId, groups, config.collectionContext);

  const associatedDataRecipients = useMemo(() => {
    return getAssociatedDataRecipientsForDisclosure(dataMap, disclosureData.dataRecipients, group);
  }, [dataMap, disclosureData.dataRecipients, group]);
  const unassociatedDataRecipients = useMemo(() => {
    const associatedVendorIds = new Set(associatedDataRecipients.map((dr) => dr.vendorId));
    return disclosureData.dataRecipients.filter((dr) => !associatedVendorIds.has(dr.vendorId));
  }, [associatedDataRecipients, disclosureData.dataRecipients]);

  // TODO: add the callouts e.g. for the COLLECTION_FOR_OTHER_BUSINESSES_SLUG from ConsumerDataDisclosuresPage.tsx

  const isPageDataLoaded = Boolean(
    group &&
      disclosureData &&
      disclosureData.requests.dataMapRequest.success &&
      disclosureData.requests.recipientsRequest.success &&
      contextGroupsRequest.success,
  );
  const completionState = useMemo(
    () =>
      getDisclosureCompletionState(
        dataMap,
        associatedDataRecipients,
        groups,
        disclosureData.collectedPICGroups,
      ),
    [dataMap, associatedDataRecipients, groups, disclosureData.collectedPICGroups],
  );
  // While loading, report "complete" so the footer doesn't flash; once loaded, an incomplete
  // step keeps its forward CTA even for orgs past onboarding.
  const showOnboardingFooter = useDataFlowsOnboardingFooterGate(
    config.collectionContext.includes("EMPLOYEE"),
    !isPageDataLoaded || completionState.isComplete,
  );
  const invalidateOnboardingProgress = useInvalidateOnboardingProgress(orgId);

  if (!isPageDataLoaded) {
    return <PageLoading />;
  }

  const { isComplete, incompleteGroups, recipientsWithNoDisclosedPIC } = completionState;

  const nextDisabledTooltipParts: string[] = [];
  if (incompleteGroups.length > 0) {
    nextDisabledTooltipParts.push(
      `Please associate each personal information category with the appropriate vendor. \n Unassociated categories: ${incompleteGroups
        .map((group) => group.name)
        .join(", ")}`,
    );
  }
  if (recipientsWithNoDisclosedPIC.length > 0) {
    nextDisabledTooltipParts.push(
      `Please select at least one personal information category for each vendor. \n Unselected vendors: ${recipientsWithNoDisclosedPIC
        .map((recipient) => recipient.name)
        .join(", ")}`,
    );
  }
  const nextDisabledTooltip =
    nextDisabledTooltipParts.length > 0 ? nextDisabledTooltipParts.join("\n\n") : undefined;

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showOnboardingFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Data Disclosures"
                nextOnClick={() => {
                  invalidateOnboardingProgress();
                  history.push(config.dashboardRoute);
                }}
                nextDisabled={!isComplete}
                nextDisabledTooltip={nextDisabledTooltip}
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <div className="mb-[32px]">
          <Breadcrumbs
            hideHomeIcon={true}
            title={`Data Disclosures - ${group.name}`}
            source={config.breadcrumbSource}
            sourceRoute={config.breadcrumbSourceRoute}
          />
        </div>
        {config.headerElement}
        <GroupedVendorDisclosureCards
          associatedDataRecipients={associatedDataRecipients}
          unassociatedDataRecipients={unassociatedDataRecipients}
          dataMap={dataMap}
          groups={groups}
          disclosureData={disclosureData}
          patchDataMap={patchDataMap}
          dashboardRoute={config.dashboardRoute}
          pageContext={config.collectionContext[0]}
          hasMultipleGroups={hasMultipleGroups}
          showHrHelpText={config.collectionContext.includes("EMPLOYEE")}
        />
      </div>
    </StandardPageWrapper>
  );
}
